import * as React from "react";
import Alert from "@mui/material/Alert";
import { AxiosError } from "axios";
import Container from "@mui/material/Container";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Paper from "@mui/material/Paper";
import FormGroup from "@mui/material/FormGroup";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import { NavLink, useParams, useNavigate } from "react-router-dom";
import * as datefns from "date-fns";
import * as rhf from "react-hook-form";

import { PageContainer } from "../common/page_container";
import { PageTitle } from "../common/page_title";
import { PatientFileTemplate } from "./api_types";
import * as gc from "../state/global_context";

interface InfoMessage {
  severity: "success" | "error";
  message: string;
}

//const DeletePatientFileButton = ({
//  patientFile,
//  onDelete,
//}: {
//  patientFile: PatientFile;
//  onDelete?: (patientFileId: number) => void;
//}) => {
//  const [open, setOpen] = React.useState(false);
//  const [confirmText, setConfirmText] = React.useState("");
//  const [globalState] = gc.useGlobalContext();
//
//  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
//    event.preventDefault();
//    if (confirmText !== "delete") {
//      return;
//    }
//
//    await globalState.mportalEndpoint.delete(
//      `office/patient-files/${patientFile.id}`
//    );
//
//    if (onDelete) {
//      onDelete(patientFile.id);
//    }
//  };
//
//  const handleClose = () => {
//    setOpen(false);
//    setConfirmText("");
//  };
//
//  return (
//    <span>
//      <Button
//        variant="outlined"
//        color="secondary"
//        onClick={() => setOpen(true)}
//        size="small"
//      >
//        Delete
//      </Button>
//      <Dialog open={open} onClose={handleClose}>
//        <form onSubmit={handleSubmit}>
//          <FormGroup>
//            <DialogContent>
//              <DialogContentText>
//                Delete this file? This action cannot be undone. To delete, type
//                &quot;delete&quot; and click delete
//              </DialogContentText>
//
//              <TextField
//                value={confirmText}
//                label='type "delete" here'
//                onChange={(event) => setConfirmText(event.target.value)}
//              />
//            </DialogContent>
//            <DialogActions>
//              <Box
//                display="flex"
//                justifyContent="space-between"
//                sx={{ spacing: 1 }}
//              >
//                <Button onClick={handleClose} color="secondary">
//                  Cancel
//                </Button>
//                <Button type="submit" color="primary">
//                  Delete
//                </Button>
//              </Box>
//            </DialogActions>
//          </FormGroup>
//        </form>
//      </Dialog>
//    </span>
//  );
//};

interface EditPatientFileTemplateFormData {
  name: string;
  summary: string;
  notes: string;
}

type ManagePatientFileTemplateParams = {
  templateId: string;
};

export const ManagePatientFileTemplate = () => {
  const params = useParams<ManagePatientFileTemplateParams>();

  const [template, setTemplate] = React.useState<PatientFileTemplate | null>(
    null
  );
  const [globalState] = gc.useGlobalContext();
  const [errorMessage, setErrorMessage] = React.useState<InfoMessage>({
    severity: "success",
    message: "",
  });
  const form = rhf.useForm<EditPatientFileTemplateFormData>();
  const navigate = useNavigate();

  const getTemplate = async () => {
    const res = await globalState.mportalEndpoint.get<PatientFileTemplate>(
      `office/patient-file-templates/${params.templateId}`
    );
    setTemplate(res.data);
  };

  React.useEffect(() => {
    getTemplate();
  }, [params.templateId]);

  if (template === null) {
    return (
      <Container maxWidth="xl" sx={{ p: 2 }}>
        <p>Loading...</p>
      </Container>
    );
  }

  const onDelete = () => {
    navigate(`/admin/patient-file-categories`);
  };

  const onSubmit = form.handleSubmit(
    async (data: EditPatientFileTemplateFormData) => {
      const update: PatientFileTemplate = {
        id: template.id,
        name: data.name,
        summary: data.summary,
        notes: data.notes,
      };

      try {
        await globalState.mportalEndpoint.put<PatientFileTemplate>(
          `office/patient-file-templates/${template.id}`,
          update
        );
        setErrorMessage({ severity: "success", message: "Template updated" });
      } catch (error) {
        setErrorMessage({
          severity: "error",
          message:
            error instanceof AxiosError
              ? "Error: " + error.response?.data.toString()
              : "Error",
        });
      }
    }
  );

  return (
    <PageContainer>
      <PageTitle>
          <NavLink to={`/admin/patient-file-categories`}>
            Manage Patient File Templates and Categories
          </NavLink>{" "} /
      Manage Patient File Template</PageTitle>

      <Paper
        sx={{ marginTop: 1, minHeight: 100, p: 1, fontFamily: "Monospace" }}
      >
        {errorMessage.message.length === 0 ? (
          ""
        ) : (
          <Alert severity={errorMessage.severity} sx={{ marginBottom: 2 }}>
            {errorMessage.message}
          </Alert>
        )}

        <form onSubmit={onSubmit}>
          <Stack spacing={1}>
            <rhf.Controller
              control={form.control}
              name="name"
              defaultValue={template.name}
              shouldUnregister={true}
              render={({ field, fieldState }) => (
                <TextField
                  label="Name"
                  sx={{ maxWidth: 400 }}
                  {...field}
                  error={!!fieldState.error}
                />
              )}
            />

            <rhf.Controller
              control={form.control}
              name="summary"
              defaultValue={template.summary}
              shouldUnregister={true}
              render={({ field, fieldState }) => (
                <TextField
                  label="Summary"
                  sx={{ maxWidth: 400 }}
                  {...field}
                  error={!!fieldState.error}
                />
              )}
            />

            <rhf.Controller
              control={form.control}
              name="notes"
              defaultValue={template.notes}
              shouldUnregister={true}
              render={({ field, fieldState }) => (
                <TextField
                  label="Note"
                  multiline
                  {...field}
                  error={!!fieldState.error}
                  fullWidth
                />
              )}
            />
            <Button
              sx={{ marginTop: 2, maxWidth: 200 }}
              type="submit"
              variant="contained"
              color="primary"
              disabled={!form.formState.isDirty}
            >
              Save
            </Button>
          </Stack>
        </form>
      </Paper>
    </PageContainer>
  );
};
